// src/features/executive/performance/widgets/RevenueSummaryTile.tsx
import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import KPICard from 'features/executive/components/KPICard/KPICard'
import KPIDetailRow from 'features/executive/components/KPICard/KPIDetailRow'
import { useHero } from 'features/executive/components/WidgetCard/HeroContext'
import { useRevenueSummary } from '../hooks/use-revenue-summary'
import {
  achievementColor,
  formatCurrency,
} from 'utils/executive.utils'

// Small inner component so it can call useHero() inside the same HeroContext
// the KPICard provides. Renders the AR snapshot row with hero-aware colors.
function ArSnapshotRow({
  outstandingRemaining,
  outstandingOrderCount,
  t,
}: {
  outstandingRemaining: number
  outstandingOrderCount: number
  t: (key: string, fallback?: string) => string
}) {
  const { onHero } = useHero()
  const hasOutstanding = outstandingRemaining > 0
  const bgColor = onHero
    ? hasOutstanding
      ? 'rgba(255,255,255,0.18)'
      : 'transparent'
    : hasOutstanding
    ? 'warning.lighter'
    : 'transparent'
  const fgColor = onHero
    ? '#FFFFFF'
    : hasOutstanding
    ? 'warning.dark'
    : 'success.main'
  return (
    <Tooltip
      title={t(
        'EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.OUTSTANDING_REMAINING_TOOLTIP',
        'ยอดที่คนไข้ค้างชำระทั้งหมด ณ ปัจจุบัน — รวมทุกใบเสร็จที่ยังเก็บไม่ครบ\n(ไม่ขึ้นกับช่วงเวลาที่เลือก — เป็นยอดสะสม)\n(ไม่ขึ้นกับตัวกรอง แพทย์ / หัตถการ / ประเภทคนไข้ — เป็นยอดค้างทั้งสาขาที่เลือก)'
      )}
      arrow
      placement="top"
      componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          cursor: 'help',
          backgroundColor: bgColor,
          borderRadius: 0.5,
          px: 0.5,
          py: 0.25,
          ml: -0.5,
          mr: -0.5,
        }}
      >
        <Typography variant="caption" sx={{ color: fgColor, fontWeight: 500 }}>
          {hasOutstanding ? '⚠️ ' : '✓ '}
          {t('EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.OUTSTANDING_REMAINING', 'ค้างชำระคงเหลือ')}
          {hasOutstanding && ` (${outstandingOrderCount} ${t('EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.ORDERS', 'ออร์เดอร์')})`}
        </Typography>
        <Typography variant="caption" sx={{ color: fgColor, fontWeight: 600 }}>
          {hasOutstanding ? formatCurrency(outstandingRemaining) : t('EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.OUTSTANDING_NONE', 'ไม่มี')}
        </Typography>
      </Box>
    </Tooltip>
  )
}

export default function RevenueSummaryTile() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useRevenueSummary()

  const isEmpty = !data
  const pct =
    data && data.target != null && data.target > 0
      ? (data.actual / data.target) * 100
      : null

  const targetVariance =
    data && data.target != null ? data.actual - data.target : null
  const targetVariancePct =
    data && data.target != null && data.target > 0
      ? (data.actual - data.target) / data.target
      : null

  const formatSignedCurrency = (v: number | null): string => {
    if (v == null) return '—'
    const sign = v > 0 ? '+' : v < 0 ? '-' : ''
    return `${sign}${formatCurrency(Math.abs(v))}`
  }

  const bd = data?.breakdown ?? null

  const sourceTooltip = t(
    'EXECUTIVE.PERFORMANCE.REVENUE.SOURCE_TOOLTIP',
    'ยอดสุทธิตามใบเสร็จ:\n• ใบเสร็จปกติ (หัก GV + โปรโมชั่นแล้ว)\n• เก็บค้างชำระ BL\n• ไม่รวมเงินรับล่วงหน้า AD (ยังไม่นับเป็นรายได้)'
  )

  return (
    <KPICard
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      skeletonHeight={140}
      title={t('EXECUTIVE.PERFORMANCE.REVENUE.TITLE')}
      value={data ? formatCurrency(data.actual) : '—'}
      subtitle={
        data && data.target != null
          ? t('EXECUTIVE.PERFORMANCE.REVENUE.ACHIEVEMENT', { pct: pct?.toFixed(1) ?? '—' })
          : undefined
      }
      progressPct={pct}
      color={achievementColor(pct)}
      headerAction={
        <Tooltip
          title={sourceTooltip}
          arrow
          placement="top"
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
        >
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.disabled', cursor: 'help' }}
          />
        </Tooltip>
      }
      caption={
        data ? (
          <Stack spacing={0.5}>
            <KPIDetailRow
              label={t('EXECUTIVE.PERFORMANCE.REVENUE.TARGET')}
              value={formatCurrency(data.target)}
            />
            {targetVariance != null && (
              <KPIDetailRow
                label={t('EXECUTIVE.PERFORMANCE.REVENUE.VS_TARGET', 'เทียบเป้า')}
                value={formatSignedCurrency(targetVariance)}
                variance={targetVariancePct}
              />
            )}
            <KPIDetailRow
              label={t('EXECUTIVE.PERFORMANCE.REVENUE.VS_PREV', 'vs งวดก่อน')}
              variance={data.variance}
            />

            {bd && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Stack spacing={0.5}>
                  <KPIDetailRow
                    label={`${t('EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.NORMAL', 'ใบเสร็จปกติ')}${bd.normalReceiptCount > 0 ? ` (${bd.normalReceiptCount})` : ''}`}
                    value={formatCurrency(bd.normalRevenue)}
                  />
                  {bd.outstandingCollected > 0 && (
                    <KPIDetailRow
                      label={`${t('EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.OUTSTANDING', 'เก็บค้าง (BL)')}${bd.outstandingReceiptCount > 0 ? ` (${bd.outstandingReceiptCount})` : ''}`}
                      value={formatCurrency(bd.outstandingCollected)}
                    />
                  )}
                  {(bd.promotionDiscount > 0 || bd.voucherDiscount > 0) && (
                    <KPIDetailRow
                      label={t('EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.DISCOUNT', 'หัก GV+โปรโมชั่น')}
                      value={`− ${formatCurrency(bd.promotionDiscount + bd.voucherDiscount)}`}
                    />
                  )}
                  {bd.advanceDeposit > 0 && (
                    <KPIDetailRow
                      label={`${t('EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.ADVANCE', 'รับล่วงหน้า (AD)')} (${bd.advanceReceiptCount} ${t('EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.RECEIPTS', 'ใบ')})`}
                      labelTooltip={t(
                        'EXECUTIVE.PERFORMANCE.REVENUE.BREAKDOWN.ADVANCE_TOOLTIP',
                        'เงินที่คนไข้ฝากไว้ใช้ในอนาคต — ยังไม่นับเป็นรายได้จนกว่าจะใช้บริการ'
                      )}
                      value={`+ ${formatCurrency(bd.advanceDeposit)}`}
                    />
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <ArSnapshotRow
                    outstandingRemaining={bd.outstandingRemaining}
                    outstandingOrderCount={bd.outstandingOrderCount}
                    t={t as unknown as (k: string, fb?: string) => string}
                  />
                </Stack>
              </>
            )}
          </Stack>
        ) : undefined
      }
    />
  )
}
